import { useState } from "react";
import { Wallet2, ChevronDown, AlertTriangle, Bell, BellRing, BellOff } from "lucide-react";
import { Wishbone } from "./Wishbone";
import { WalletModal } from "./WalletModal";
import { useWallet } from "../../lib/wallet";
import { useConfig, useStats } from "../../lib/queries";
import { fmtUsd, short } from "../../lib/format";

const Stat = ({ label, value, testId }) => (
  <div className="hidden xl:flex flex-col leading-none">
    <span className="eyebrow">{label}</span>
    <span className="mono text-xs text-white mt-1" data-testid={testId}>{value}</span>
  </div>
);

export const Header = ({ alerts }) => {
  const [open, setOpen] = useState(false);
  const w = useWallet();
  const { data: cfg } = useConfig();
  const { data: st } = useStats();

  return (
    <header className="h-14 shrink-0 flex items-center justify-between px-4 border-b border-line bg-s1/90 backdrop-blur-md z-40" data-testid="terminal-header">
      <div className="flex items-center gap-3">
        <Wishbone size={32} />
        <div className="leading-none">
          <div className="font-heading font-bold text-[17px] tracking-tight text-white">CROSS</div>
          <div className="eyebrow mt-0.5">Pons OTC Escrow Terminal</div>
        </div>
        <div className="hidden md:flex items-center gap-2 ml-5 pl-5 border-l border-line">
          <span className="w-1.5 h-1.5 rounded-full bg-pos animate-pulseDot" />
          <span className="mono text-[11px] text-slate-300" data-testid="network-badge">{cfg?.chain_name || "Robinhood Chain"} · ID {cfg?.chain_id ?? 4663}</span>
          <span className="tag border-violet/40 text-violet-300">{cfg?.dex || "Uniswap v4"}</span>
          {cfg?.simulated && <span className="tag border-warn/50 text-warn bg-warn/15 font-semibold">SIM</span>}
        </div>
      </div>

      <div className="flex items-center gap-3 lg:gap-8">
        <Stat label="OTC vol 24h" value={fmtUsd(st?.volume_24h_usd)} testId="stat-vol-24h" />
        <Stat label="Impact saved (all)" value={fmtUsd(st?.slippage_saved_usd)} testId="stat-saved-total" />
        <Stat label="Locked in escrow" value={fmtUsd(st?.locked_notional_usd)} testId="stat-locked" />
        <Stat label="$CROSS" value={`${cfg ? fmtUsd(cfg.cross_price_usd, 2) : "—"} · fee ${cfg ? cfg.fee_bps / 100 : "—"}%`} testId="stat-cross" />

        {w.wrongNetwork && (
          <button onClick={w.switchNetwork} className="btn h-8 px-3 border border-warn/50 text-warn text-xs hover:bg-warn/10" data-testid="switch-network-btn">
            <AlertTriangle size={13} /> Wrong network — switch
          </button>
        )}
        {w.connected && alerts?.supported && (
          <button
            onClick={() => { if (alerts.permission === "default") alerts.requestPermission(); }}
            title={alerts.enabled ? "Counterparty alerts on — you'll be pinged when the other side funds"
              : alerts.permission === "denied" ? "Alerts blocked — enable notifications for this site in your browser"
              : "Enable counterparty funding alerts"}
            className={`btn h-9 w-9 px-0 border transition-colors ${alerts.enabled ? "border-violet/50 text-violet-300 bg-violet/10" : "border-line2 text-fog hover:text-white hover:border-violet"}`}
            data-testid="alerts-toggle-btn"
          >
            {alerts.enabled ? <BellRing size={15} /> : alerts.permission === "denied" ? <BellOff size={15} /> : <Bell size={15} />}
          </button>
        )}
        <button onClick={() => setOpen(true)} className={w.connected ? "btn-ghost h-9 px-3" : "btn-primary h-9"} data-testid="wallet-connect-header-btn">
          <Wallet2 size={15} />
          {w.connected ? (
            <span className="flex items-center gap-2">
              <span className="mono text-xs" data-testid="wallet-address-display">{short(w.address)}</span>
              <span className={`tag ${w.kind === "demo" ? "border-warn/40 text-warn" : "border-pos/40 text-pos"}`}>{w.kind}</span>
              <ChevronDown size={13} className="text-fog" />
            </span>
          ) : "Connect wallet"}
        </button>
      </div>
      <WalletModal open={open} onClose={() => setOpen(false)} />
    </header>
  );
};
